import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LoadingService } from './services/loading.service';
import { ThemeService } from './services/theme.service';
import { MenuItem } from 'primeng/api';
import { UserEventHistoryService } from './services/user-event-history.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'iima';

  currentTheme: 'aura-then' | 'noir' = 'noir';

  sidebarMode: 'narrow' | 'expanded' | 'hidden' = 'expanded';
  isHovering = false;
  isMobile = false;

  isLoginPage = false;
  isLandingPage = false;
  showBackButton = false;

  currentSubMenu = '';
  activeMenuWithSubItems: MenuItem | null = null;
  showSubMenuTabs = false;

  // Menu structure (your original menu)
  menuItems: MenuItem[] = [
    { label: 'Home', icon: 'pi pi-home', routerLink: ['/dashboard'] },
    { label: 'Master', icon: 'pi pi-database', routerLink: ['/master'] },
    {
      label: 'Revenue',
      icon: 'pi pi-wallet',
      items: [
        { label: 'Dashboard', routerLink: ['/revenue/dashboard'] },
        { label: 'Proposals', routerLink: ['/scheme/revenue'] },
      ],
    },
    {
      label: 'IDEX',
      icon: 'pi pi-bolt',
      items: [
        { label: 'Dashboard', routerLink: ['/idex/dashboard'] },
        { label: 'Subschemes', routerLink: ['/scheme/idex'] },
        { label: 'Monitoring', routerLink: ['/idex/monitoring'] },
        { label: 'Review', routerLink: ['/idex/review'] },
      ],
    },
    {
      label: 'Make',
      icon: 'pi pi-briefcase',
      items: [
        { label: 'Dashboard', routerLink: ['/make/dashboard'] },
        { label: 'Subschemes', routerLink: ['/scheme/make'] },
      ],
    },
    {
      label: 'TDF',
      icon: 'pi pi-sliders-h',
      items: [
        { label: 'Dashboard', routerLink: ['/tdf/dashboard'] },
        { label: 'Proposals', routerLink: ['/scheme/tdf'] },
      ],
    },
    { label: 'Event History', icon: 'pi pi-history', routerLink: ['/event-history'] },
    { label: 'Proposal', icon: 'pi pi-file', routerLink: ['/master/proposal'] },
    {
      label: 'DMS',
      fullform: 'Document Managemnet System',
      icon: 'pi pi-folder-open',
      routerLink: ['/dmp'],
    },
    { label: 'Setup', icon: 'pi pi-cog', routerLink: ['/setup'] },
  ];

  constructor(
    private themeService: ThemeService,
    private router: Router,
    public loadingService: LoadingService,
    private readonly userEventHistoryService: UserEventHistoryService
  ) {
    // Back button logic
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        this.showBackButton =
          url.startsWith('/master') && url !== '/master' && url !== '/master/proposal';
      }
    });
  }

  ngOnInit(): void {
    // Theme (optional - keep if you want)
    // this.themeService.initTheme();
    // this.themeService.theme$.subscribe(theme => this.currentTheme = theme);

    // Handle initial screen size and resize
    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());

    // Monitor route changes for login/landing
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.isLoginPage = event.url.includes('/login');
        this.isLandingPage = event.url.includes('/landing');
        this.trackPageVisit(event.urlAfterRedirects || event.url);
      });

    // Check current URL on init
    this.isLoginPage = this.router.url.includes('/login');
    this.isLandingPage = this.router.url.includes('/landing');

    // Permissions init
    this.initMenuWithPermissions();

    // Restore submenu state from localStorage on refresh
    this.restoreSubmenuState();
  }

  private trackPageVisit(url: string): void {
    if (url.includes('/login') || url.includes('/landing')) {
      return;
    }

    this.userEventHistoryService.trackEvent('PAGE_VISIT', url, 'Navigated inside application');
  }

  // ✅ Detect mobile and default sidebar mode
  private handleResize(): void {
    this.isMobile = window.innerWidth < 768;

    if (this.isMobile) {
      // Mobile: default hidden
      this.sidebarMode = 'hidden';
    } else {
      // Desktop: default expanded (if it was hidden)
      if (this.sidebarMode === 'hidden') this.sidebarMode = 'expanded';
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  // ✅ Toggle sidebar based on mobile/desktop
  toggleSidebar(): void {
    if (this.isMobile) {
      // Mobile: hidden <-> narrow
      this.sidebarMode = this.sidebarMode === 'hidden' ? 'narrow' : 'hidden';
      return;
    }
    // Desktop: expanded <-> narrow
    this.sidebarMode = this.sidebarMode === 'expanded' ? 'narrow' : 'expanded';
  }

  goBackToTileView(): void {
    this.router.navigate(['/master']);
  }

  // Submenu top tabs behaviour
  onSubMenuClick(subMenuLabel: string): void {
    this.currentSubMenu = subMenuLabel;
  }

  onMenuWithSubItemsClicked(event: { menuItem: MenuItem; index: number }): void {
    this.activeMenuWithSubItems = event.menuItem;
    this.showSubMenuTabs = true;

    if (event.menuItem.items && event.menuItem.items.length > 0) {
      this.currentSubMenu = event.menuItem.items[0].label || '';
    }
  }

  onMenuWithoutSubItemsClicked(): void {
    this.showSubMenuTabs = false;
    this.activeMenuWithSubItems = null;
    this.currentSubMenu = '';
  }

  getActiveMenuName(): string {
    return this.activeMenuWithSubItems?.label || '';
  }

  getActiveSubMenuItems(): MenuItem[] {
    return this.activeMenuWithSubItems?.items || [];
  }

  getCurrentSubMenu(): string {
    return this.currentSubMenu;
  }

  getActiveMenuIcon(): string {
    return this.activeMenuWithSubItems?.icon || 'pi pi-wallet';
  }

  // ✅ MAIN LAYOUT CLASS: matches sidebar widths (0 / 24 / 72)
  getMainLayoutClass(): string {
    // hidden
    if (this.sidebarMode === 'hidden') return 'ml-0 w-full';

    // expanded OR narrow+hover => expanded width (w-72 = 18rem)
    if (this.sidebarMode === 'expanded' || (this.sidebarMode === 'narrow' && this.isHovering)) {
      return 'ml-72 w-[calc(100%-18rem)]';
    }

    // narrow (w-24 = 6rem)
    return 'ml-24 w-[calc(100%-6rem)]';
  }

  // ---------- Local Storage restore ----------
  private restoreSubmenuState(): void {
    const savedSubmenu = localStorage.getItem('activeSubmenuIndex');
    if (!savedSubmenu) return;

    try {
      const activeSubmenuIndex = JSON.parse(savedSubmenu);
      const menuIndex = activeSubmenuIndex.menuIndex;

      if (menuIndex >= 0 && menuIndex < this.menuItems.length) {
        const menuItem = this.menuItems[menuIndex];

        if (menuItem.items && menuItem.items.length > 0) {
          this.activeMenuWithSubItems = menuItem;
          this.showSubMenuTabs = true;

          const subIndex = activeSubmenuIndex.subIndex;
          if (subIndex !== undefined && subIndex >= 0 && subIndex < menuItem.items.length) {
            this.currentSubMenu = menuItem.items[subIndex].label || '';
          } else {
            this.currentSubMenu = menuItem.items[0].label || '';
          }
        }
      }
    } catch (e) {
      console.error('Error parsing saved submenu state:', e);
      localStorage.removeItem('activeSubmenuIndex');
    }
  }

  // ---------- Permissions-based menu enrichment ----------
  private initMenuWithPermissions(retryCount = 0): void {
    const storedPermissions = sessionStorage.getItem('user_permissions');
    if (storedPermissions) {
      const parsedPermissions = JSON.parse(storedPermissions);
      this.addSchemeIdsToMenu(parsedPermissions);
    } else if (retryCount < 5) {
      setTimeout(() => this.initMenuWithPermissions(retryCount + 1), 300);
    }
  }

  private addSchemeIdsToMenu(permissions: any[]): void {
    this.menuItems = this.menuItems.map((menuItem) => {
      const matched = permissions.find(
        (p: any) => p.name?.toLowerCase() === menuItem.label?.toLowerCase()
      );

      if (matched) {
        // top-level
        if (matched.scheme_id) {
          menuItem.routerLink = [matched.url];
          menuItem.queryParams = { scheme_id: matched.scheme_id };
        }

        // scheme submenus
        const schemeMenus = ['revenue', 'idex', 'make', 'tdf'];
        if (schemeMenus.includes(matched.name.toLowerCase()) && matched.sub_menus?.length) {
          menuItem.items = matched.sub_menus.map((sub: any) => {
            const subMenuItem: MenuItem = {
              label: sub.name,
              routerLink: [sub.url],
              queryParams: matched.scheme_id ? { scheme_id: matched.scheme_id } : undefined,
            };
            return subMenuItem;
          });
        }
      }
      return menuItem;
    });
  }
  // initialData = {};
  // isUpdatedialog = true;
  // updateDialogConfig: DialogConfig = {
  //   title: 'Customer Onboarding Form',
  //   description: 'Fill customer details',
  //   width: '80vw',
  //   formLayout: 'two-column',
  //   submitButtonText: 'Save Customer',
  //   cancelButtonText: 'Cancel',
  // submit: {
  //   api: "/api/customers",
  //   method: "POST"
  // },
  //   fields: [

  //     {
  //       key: "customer_name",
  //       label: "Customer Name",
  //       type: "text",
  //       placeholder: "Enter customer name",
  //       validation: {
  //         required: true,
  //         minLength: 3,
  //         maxLength: 100
  //       }
  //     },

  //     {
  //       key: "email",
  //       label: "Email",
  //       type: "email",
  //       placeholder: "Enter email",
  //       validation: {
  //         required: true,
  //         pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"
  //       }
  //     },

  //     {
  //       key: "phone",
  //       label: "Phone Number",
  //       type: "text",
  //       placeholder: "Enter phone",
  //       validation: {
  //         required: true,
  //         pattern: "^[0-9]{10}$"
  //       }
  //     },

  //     {
  //       key: "customer_type",
  //       label: "Customer Type",
  //       type: "radio",
  //       options: [
  //         { label: "Individual", value: "individual" },
  //         { label: "Business", value: "business" }
  //       ],
  //       validation: {
  //         required: true
  //       }
  //     },

  //     {
  //       key: "company_name",
  //       label: "Company Name",
  //       type: "text",
  //       placeholder: "Enter company name",
  //       visibleWhen: {
  //         field: "customer_type",
  //         value: "business"
  //       }
  //     },

  //     {
  //       key: "country",
  //       label: "Country",
  //       type: "select",
  //       master: {
  //         api: "/api/masters/country",
  //         labelField: "name",
  //         valueField: "id"
  //       },
  //       validation: {
  //         required: true
  //       }
  //     },

  //     {
  //       key: "state",
  //       label: "State",
  //       type: "select",
  //       dependsOn: "country",
  //       dependsParam: "country_id",
  //       master: {
  //         api: "/api/masters/state",
  //         labelField: "name",
  //         valueField: "id"
  //       }
  //     },

  //     {
  //       key: "city",
  //       label: "City",
  //       type: "select",
  //       dependsOn: "state",
  //       dependsParam: "state_id",
  //       master: {
  //         api: "/api/masters/city",
  //         labelField: "name",
  //         valueField: "id"
  //       }
  //     },

  //     {
  //       key: "area",
  //       label: "Area",
  //       type: "select",
  //       dependsOn: "city",
  //       dependsParam: "city_id",
  //       master: {
  //         api: "/api/masters/area",
  //         labelField: "name",
  //         valueField: "id"
  //       }
  //     },

  //     {
  //       key: "address",
  //       label: "Address",
  //       type: "textarea",
  //       placeholder: "Enter full address",
  //       validation: {
  //         required: true,
  //         maxLength: 500
  //       }
  //     },

  //     {
  //       key: "dob",
  //       label: "Date of Birth",
  //       type: "date"
  //     },

  //     {
  //       key: "credit_limit",
  //       label: "Credit Limit",
  //       type: "number",
  //       placeholder: "Enter credit limit",
  //       validation: {
  //         required: true
  //       }
  //     },

  //     {
  //       key: "payment_terms",
  //       label: "Payment Terms",
  //       type: "select",
  //       options: [
  //         { label: "Advance", value: "advance" },
  //         { label: "15 Days", value: "15_days" },
  //         { label: "30 Days", value: "30_days" }
  //       ]
  //     },

  //     {
  //       key: "preferred_contact",
  //       label: "Preferred Contact",
  //       type: "checkbox"
  //     },

  //     {
  //       key: "contact_methods",
  //       label: "Contact Methods",
  //       type: "radio",
  //       options: [
  //         { label: "Email", value: "email" },
  //         { label: "Phone", value: "phone" },
  //         { label: "SMS", value: "sms" }
  //       ]
  //     },

  //     {
  //       key: "notes",
  //       label: "Internal Notes",
  //       type: "text-editor"
  //     }

  //   ]
  // };

  // onSmitData(data: any) {
  //   console.log(data);
  // }

}
